import { defineQuery, defineQueryOptions } from '@pinia/colada'
import type { PaAPI, RefDataModel } from '@rialtic/types'
import { STALE_TIME, useSsrQueryCache } from '~/queries/query.helpers'

const CONTENT_DATA_QUERY_KEYS_ROOT = ['paapi', 'content-data'] as const
export const CONTENT_DATA_QUERY_KEYS = {
  root: CONTENT_DATA_QUERY_KEYS_ROOT,
  /** Edit types AKA Logic types */
  editTypes: [...CONTENT_DATA_QUERY_KEYS_ROOT, 'edit-types'] as const,

  /** Ref Data */
  refData: (modelName: RefDataModel) =>
    [...CONTENT_DATA_QUERY_KEYS_ROOT, 'ref-data', modelName] as const,

  carcs: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'ref-data',
    'ReasonCodeMetadata',
    'carcs',
  ] as const,
  rarcs: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'ref-data',
    'ReasonCodeMetadata',
    'rarcs',
  ] as const,

  /** Engines */
  engines: [...CONTENT_DATA_QUERY_KEYS_ROOT, 'engines'] as const,

  /** Source Documents */
  sourceDocuments: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'source-documents',
  ] as const,

  /** Source types */
  sourceTypeLevelOnes: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'source-type-level-one',
  ] as const,
  sourceTypeLevelTwos: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'source-type-level-two',
  ],
  sourceTypeLevelTwosByLevelOneId: (id: string) => [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'source-type-level-two',
    id,
  ],
  sourceTypeRegions: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'source-regions',
  ] as const,

  /** Topics */
  topics: [...CONTENT_DATA_QUERY_KEYS_ROOT, 'topics'] as const,
}

/** Edit types AKA Logic types */
export const useEditTypes = defineQuery({
  key: CONTENT_DATA_QUERY_KEYS.editTypes,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/edit-type']>('/api/paapi/edit-type')
  },
  staleTime: STALE_TIME.ONE_DAY,
})

/** Ref Data */
export const refDataQuery = defineQueryOptions(
  <TCode extends PaAPI.Get['/ref-data/{modelName}'][number]>(
    modelName: RefDataModel,
  ) => {
    return {
      key: CONTENT_DATA_QUERY_KEYS.refData(modelName),
      enabled: Boolean(modelName),
      query: () => {
        const client = useRequestFetch()
        return client<TCode[]>(`/api/paapi/ref-data/${modelName}`)
      },
      staleTime: STALE_TIME.ONE_WEEK,
    }
  },
)

export const useDiagnosisCodes = defineQuery(
  refDataQuery<{
    code: string
    description: string
    effective_start: string
    effective_end: string
  }>('Diagnosis'),
)
export const useModifierCodes = defineQuery(refDataQuery('Modifier'))
export const usePlaceOfServiceCodes = defineQuery(
  refDataQuery('PlaceOfService'),
)
export const useProcedureCodes = defineQuery(
  refDataQuery<{ code: string; type: 'CPT' | 'HPCS'; description: string }>(
    'Procedure',
  ),
)
const reasonCodeQuery = () =>
  refDataQuery<{ code: string; description: string; type: 'CARC' | 'RARC' }>(
    'ReasonCodeMetadata',
  )
export const useReasonCodes = () => useQuery(reasonCodeQuery())
export const useRevenueCodes = defineQuery(refDataQuery('Revenue'))

export type SpecialtyCode = {
  /** Not defined on object, but expected by PaAPI.Get['/ref-data/{modelName}']  */
  code: string
  id: string
  name: string
}
export const useSpecialtyCodes = defineQuery(
  refDataQuery<SpecialtyCode>('Specialty'),
)
export const useTypeOfBillCodes = defineQuery(refDataQuery('TypeOfBill'))

export const useCarcs = () =>
  useQuery({
    key: CONTENT_DATA_QUERY_KEYS.carcs,
    query: async () => {
      const queryCache = useSsrQueryCache()
      const { data } = await queryCache.refresh(
        queryCache.ensure(reasonCodeQuery()),
      )
      return data?.filter((code) => code.type === 'CARC') ?? []
    },
    staleTime: STALE_TIME.ONE_WEEK,
  })
export const useRarcs = () =>
  useQuery({
    key: CONTENT_DATA_QUERY_KEYS.rarcs,
    query: async () => {
      const queryCache = useSsrQueryCache()
      const { data } = await queryCache.refresh(
        queryCache.ensure(reasonCodeQuery()),
      )
      return data?.filter((code) => code.type === 'RARC') ?? []
    },
    staleTime: STALE_TIME.ONE_WEEK,
  })

/** Engine Queries */
export const useEngines = defineQuery({
  key: CONTENT_DATA_QUERY_KEYS.engines,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/engine']>('/api/paapi/engine')
  },
  staleTime: STALE_TIME.ONE_DAY,
})

/** Source Document Queries */
export const useSourceDocuments = defineQuery({
  key: CONTENT_DATA_QUERY_KEYS.sourceDocuments,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/source-document']>('/api/paapi/source-document')
  },
  staleTime: STALE_TIME.FIFTEEN_MINUTES,
})

/** Source Type Queries */
const sourceTypeLevelOneQuery = defineQueryOptions({
  key: CONTENT_DATA_QUERY_KEYS.sourceTypeLevelOnes,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/source-type-level-one']>(
      '/api/paapi/source-type-level-one',
    )
  },
  staleTime: STALE_TIME.ONE_DAY,
})
export const useSourceTypeLevelOnes = defineQuery(sourceTypeLevelOneQuery)

const sourceTypeLevelTwoQuery = defineQueryOptions({
  key: CONTENT_DATA_QUERY_KEYS.sourceTypeLevelTwos,
  query: async () => {
    const queryCache = useSsrQueryCache()
    const { data } = await queryCache.refresh(
      queryCache.ensure(sourceTypeLevelOneQuery),
    )
    const client = useRequestFetch()
    const levelTwos = await client<PaAPI.Get['/source-type-level-two']>(
      '/api/paapi/source-type-level-two',
    )

    return levelTwos.map((levelTwo) => {
      const levelOne = data?.find(
        (levelOne) => levelOne.id === levelTwo.source_type_level_1_id,
      )
      return { ...levelTwo, source_type_level_1: levelOne }
    })
  },
  staleTime: STALE_TIME.ONE_DAY,
})

export const useSourceTypeLevelTwos = defineQuery(sourceTypeLevelTwoQuery)

export const sourceTypeLevelTwosByLevelOneId = defineQueryOptions(
  (id: string) => {
    return {
      key: CONTENT_DATA_QUERY_KEYS.sourceTypeLevelTwosByLevelOneId(id),
      enabled: Boolean(id),
      query: async () => {
        const queryCache = useSsrQueryCache()
        const { data } = await queryCache.refresh(
          queryCache.ensure(sourceTypeLevelTwoQuery),
        )

        const levelTwos = data?.filter(
          (item) => item.source_type_level_1_id === id,
        )

        return levelTwos
      },
      staleTime: STALE_TIME.ONE_DAY,
    }
  },
)

export const useSourceTypeRegions = defineQuery({
  key: CONTENT_DATA_QUERY_KEYS.sourceTypeRegions,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/region']>('/api/paapi/region')
  },
  staleTime: STALE_TIME.ONE_DAY,
})

/** Topic Queries */
export const useTopics = defineQuery({
  key: CONTENT_DATA_QUERY_KEYS.topics,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/topic']>('/api/paapi/topic')
  },
  staleTime: STALE_TIME.ONE_DAY,
})

/** Policy Queries */
const POLICY_QUERY_KEYS_ROOT = ['paapi', 'content-data', 'policies'] as const
export const POLICY_QUERY_KEYS = {
  root: POLICY_QUERY_KEYS_ROOT,

  byId: (id: string) => [...POLICY_QUERY_KEYS_ROOT, id] as const,
}

export const usePolicies = defineQuery({
  key: POLICY_QUERY_KEYS.root,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/policy']>('/api/paapi/policy')
  },
  staleTime: STALE_TIME.FIVE_MINUTES,
})

export const policyById = defineQueryOptions((id: string) => {
  return {
    key: POLICY_QUERY_KEYS.byId(id),
    enabled: Boolean(id),
    query: () => {
      const client = useRequestFetch()
      return client<PaAPI.Get['/policy/{id}']>(`/api/paapi/policy/${id}`)
    },
    staleTime: STALE_TIME.ONE_HOUR,
  }
})
